/**
 * Chunking utility for grouping values into manageable chunks
 */

export interface Chunk {
  type: "chunk";
  value: string; // Unique identifier for the chunk
  label: string; // Display label for the chunk
  values: string[]; // Individual values in this chunk
}

export interface IndividualItem {
  type: "individual";
  value: string;
  label: string;
}

export type ProcessedGroup = Chunk | IndividualItem;

export interface ChunkingOptions {
  /**
   * Threshold for forcing chunks. If total items exceed this, all items will be chunked.
   * @default 100
   */
  forceChunksThreshold?: number;

  /**
   * Number of chunks to create when forcing chunks (for 100+ items).
   * @default 10
   */
  targetChunks?: number;

  /**
   * Default chunk size for smaller sets.
   * @default 10
   */
  defaultChunkSize?: number;

  /**
   * Whether to always create chunks (never return individual items).
   * When true, even single values will be wrapped in chunks.
   * @default false
   */
  alwaysChunk?: boolean;
}

/**
 * Groups values into chunks for better organization and performance.
 *
 * - If there are more items than `forceChunksThreshold`, always creates chunks
 * - For numeric sequences: creates `targetChunks` chunks with equal intervals
 * - For non-numeric or smaller sets: groups into chunks of `defaultChunkSize`
 *
 * @param values - Array of string values to chunk
 * @param options - Chunking configuration options
 * @returns Array of chunks and individual items
 *
 * @example
 * ```ts
 * const values = Array.from({ length: 150 }, (_, i) => String(i + 1));
 * const chunks = groupValuesIntoChunks(values);
 * // Returns 10 chunks, each containing ~15 values
 * ```
 */
export function groupValuesIntoChunks(
  values: string[],
  options: ChunkingOptions = {}
): ProcessedGroup[] {
  const {
    forceChunksThreshold = 100,
    targetChunks = 10,
    defaultChunkSize = 10,
    alwaysChunk = false,
  } = options;

  const totalItems = values.length;
  const forceChunks = totalItems > forceChunksThreshold || alwaysChunk;

  const numericValues: number[] = [];
  const nonNumericValues: string[] = [];
  const valueToOriginal = new Map<number, string>();

  // Separate numeric and non-numeric values
  for (const value of values) {
    const num = Number(value);
    if (!isNaN(num) && isFinite(num) && value.trim() === String(num)) {
      numericValues.push(num);
      valueToOriginal.set(num, value);
    } else {
      nonNumericValues.push(value);
    }
  }

  // Sort numeric values
  numericValues.sort((a, b) => a - b);

  const result: ProcessedGroup[] = [];

  // Process numeric values
  if (numericValues.length > 0) {
    if (forceChunks || numericValues.length > forceChunksThreshold) {
      // When forcing chunks or have threshold+ numeric values, group all together
      const min = numericValues[0];
      const max = numericValues[numericValues.length - 1];
      const range = max - min + 1;
      const intervalSize = Math.ceil(range / targetChunks);

      // Create fixed intervals
      for (let chunkIndex = 0; chunkIndex < targetChunks; chunkIndex++) {
        const intervalStart = min + chunkIndex * intervalSize;
        const intervalEnd = Math.min(
          min + (chunkIndex + 1) * intervalSize - 1,
          max
        );

        // Find values that fall within this interval
        const chunkValues = numericValues.filter(
          (v) => v >= intervalStart && v <= intervalEnd
        );

        // Always create chunk, even if empty or single value
        const chunkLabel =
          chunkValues.length > 0
            ? chunkValues.length === 1
              ? valueToOriginal.get(chunkValues[0])!
              : `${valueToOriginal.get(chunkValues[0])!}-${valueToOriginal.get(
                  chunkValues[chunkValues.length - 1]
                )!}`
            : `${intervalStart}-${intervalEnd}`;

        result.push({
          type: "chunk",
          value: `chunk-${intervalStart}-${intervalEnd}`,
          label: chunkLabel,
          values: chunkValues.map((v) => valueToOriginal.get(v)!),
        });
      }
    } else {
      // For smaller sets, process sequential sequences separately
      let sequenceStart = 0;

      for (let i = 1; i <= numericValues.length; i++) {
        const isEnd = i === numericValues.length;
        const isSequential =
          !isEnd && numericValues[i] === numericValues[i - 1] + 1;

        if (isEnd || !isSequential) {
          // Process sequence from sequenceStart to i-1
          const sequence = numericValues.slice(sequenceStart, i);
          const sequenceLength = sequence.length;

          // Use chunks of defaultChunkSize
          for (let j = 0; j < sequenceLength; j += defaultChunkSize) {
            const chunk = sequence.slice(j, j + defaultChunkSize);
            const chunkStart = chunk[0];
            const chunkEnd = chunk[chunk.length - 1];

            // Always create chunk if forcing, otherwise allow individuals
            if (forceChunks || chunk.length > 1) {
              const chunkLabel =
                chunk.length === 1
                  ? valueToOriginal.get(chunkStart)!
                  : `${valueToOriginal.get(chunkStart)!}-${valueToOriginal.get(
                      chunkEnd
                    )!}`;

              result.push({
                type: "chunk",
                value: `chunk-${chunkStart}-${chunkEnd}`,
                label: chunkLabel,
                values: chunk.map((v) => valueToOriginal.get(v)!),
              });
            } else {
              // Single value - add as individual
              result.push({
                type: "individual",
                value: valueToOriginal.get(chunkStart)!,
                label: valueToOriginal.get(chunkStart)!,
              });
            }
          }

          // Start new sequence
          if (!isEnd) {
            sequenceStart = i;
          }
        }
      }
    }
  }

  // Process non-numeric values - group into chunks if forcing chunks
  if (forceChunks && nonNumericValues.length > 0) {
    for (let i = 0; i < nonNumericValues.length; i += defaultChunkSize) {
      const chunk = nonNumericValues.slice(i, i + defaultChunkSize);
      const chunkLabel =
        chunk.length === 1
          ? chunk[0]
          : `${chunk[0]}-${chunk[chunk.length - 1]}`;

      result.push({
        type: "chunk",
        value: `chunk-${i}-${i + chunk.length - 1}`,
        label: chunkLabel,
        values: chunk,
      });
    }
  } else if (!forceChunks) {
    // Only add as individual if not forcing chunks
    nonNumericValues.forEach((value) => {
      result.push({
        type: "individual",
        value,
        label: value,
      });
    });
  }

  return result;
}

/**
 * Threshold for automatically chunking groups when unique values exceed this count
 */
export const CHUNK_THRESHOLD = 100;

/**
 * Determines if chunks should be computed based on unique values count
 * @param uniqueValuesCount - Number of unique values
 * @returns true if chunks should be computed
 */
export function shouldComputeChunks(uniqueValuesCount: number): boolean {
  return uniqueValuesCount > CHUNK_THRESHOLD;
}

